import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboardStore } from '@store'
import {
	StatCard,
	AlertCard,
	ComplianceGauge,
	QuickActions
} from '@components/features/dashboard/widgets'
import {
	DonutChart,
	ProgressRing,
	LineChart
} from '@components/features/dashboard/charts'
import {
	DashboardGrid,
	WidgetContainer,
	StatsRow
} from '@components/features/dashboard/layouts'
import './Dashboard.scss'

/**
 * Executive Dashboard
 *
 * High-level strategic view for executives showing compliance scores,
 * pending decisions, risk summary, and key strategic metrics.
 */
const ExecutiveDashboard = () => {
	const navigate = useNavigate()

	// Dashboard store
	const {
		executiveOverview,
		strategicMetrics,
		riskSummary,
		pendingDecisions,
		loading,
		error,
		autoRefreshEnabled,
		fetchExecutiveDashboard,
		startAutoRefresh,
		stopAutoRefresh,
		getTimeSinceUpdate
	} = useDashboardStore()

	// Fetch dashboard data on mount
	useEffect(() => {
		fetchExecutiveDashboard()

		// Start auto-refresh every 2 minutes (executives need less frequent updates)
		const cleanup = startAutoRefresh('executive', 120000)

		return () => {
			cleanup()
			stopAutoRefresh()
		}
	}, [fetchExecutiveDashboard, startAutoRefresh, stopAutoRefresh])

	// Manual refresh handler
	const handleRefresh = useCallback(() => {
		fetchExecutiveDashboard()
	}, [fetchExecutiveDashboard])

	// Quick action handlers
	const quickActions = [
		{
			icon: '✅',
			label: 'Review Decisions',
			onClick: () => navigate('/decisions'),
			permission: null
		},
		{
			icon: '📊',
			label: 'View Reports',
			onClick: () => navigate('/reports'),
			permission: 'canViewReports'
		},
		{
			icon: '📈',
			label: 'Compliance Overview',
			onClick: () => navigate('/compliance'),
			permission: null
		},
		{
			icon: '⚠️',
			label: 'Risk Dashboard',
			onClick: () => navigate('/risk'),
			permission: null
		}
	]

	// Format pending decisions for display
	const decisionAlerts = (pendingDecisions || []).map(decision => ({
		id: decision.id,
		title: decision.title || decision.name,
		description: decision.description || `Submitted by ${decision.submitted_by_name || 'Team Member'}`,
		type: decision.priority === 'high' ? 'danger' : decision.priority === 'medium' ? 'warning' : 'info',
		action: () => navigate(`/decisions/${decision.id}`),
		actionLabel: 'Review'
	}))

	// Risk distribution for DonutChart
	const riskDistribution = riskSummary?.distribution || [
		{ name: 'Low Risk', value: riskSummary?.low_risk || 0, color: 'var(--color-success)' },
		{ name: 'Medium Risk', value: riskSummary?.medium_risk || 0, color: 'var(--color-warning)' },
		{ name: 'High Risk', value: riskSummary?.high_risk || 0, color: 'var(--color-danger)' }
	]

	// Compliance trend data
	const complianceTrendData = strategicMetrics?.compliance_trend || []
	const trendLines = [
		{ dataKey: 'score', name: 'Compliance Score', color: 'var(--color-primary)' }
	]

	// Overall compliance score
	const overallComplianceScore = executiveOverview?.compliance_score || 0

	return (
		<div className="dashboard-page executive-dashboard">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__content">
					<div>
						<h1 className="page-title">Executive Dashboard</h1>
						<nav className="breadcrumb">
							<span className="breadcrumb__item breadcrumb__item--active">Dashboard</span>
						</nav>
					</div>
					<div className="page-header__actions">
						<span className="last-updated">
							Last updated: {getTimeSinceUpdate()}
							{autoRefreshEnabled && <span className="auto-refresh-badge">Auto</span>}
						</span>
						<button
							className="btn btn--secondary btn--sm"
							onClick={handleRefresh}
							disabled={loading}
						>
							{loading ? 'Refreshing...' : 'Refresh'}
						</button>
					</div>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="alert alert--danger">
					<p>{error}</p>
					<button onClick={handleRefresh}>Retry</button>
				</div>
			)}

			{/* Stats Row */}
			<StatsRow>
				<StatCard
					title="Awaiting Decision"
					value={executiveOverview?.pending_decisions ?? '-'}
					subtitle="Items need your input"
					icon="📋"
					variant="warning"
					loading={loading}
					onClick={() => navigate('/decisions')}
				/>
				<StatCard
					title="Excused Items"
					value={executiveOverview?.excused_items ?? '-'}
					subtitle="This month"
					icon="✅"
					variant="success"
					loading={loading}
				/>
				<StatCard
					title="Active Projects"
					value={executiveOverview?.active_projects ?? '-'}
					subtitle="Company-wide"
					icon="📁"
					variant="primary"
					loading={loading}
					onClick={() => navigate('/projects')}
				/>
				<StatCard
					title="Risk Score"
					value={riskSummary?.overall_score ? `${riskSummary.overall_score}%` : '-'}
					subtitle={riskSummary?.trend === 'up' ? 'Increasing' : riskSummary?.trend === 'down' ? 'Decreasing' : 'Stable'}
					icon="⚠️"
					variant={riskSummary?.overall_score > 70 ? 'danger' : riskSummary?.overall_score > 40 ? 'warning' : 'success'}
					loading={loading}
					onClick={() => navigate('/risk')}
				/>
			</StatsRow>

			{/* Compliance Score and Pending Decisions Row */}
			<DashboardGrid columns={2}>
				{/* Overall Compliance Score */}
				<WidgetContainer
					title="Compliance Score"
					onAction={() => navigate('/compliance')}
					actionLabel="View Details"
					loading={loading}
				>
					<ComplianceGauge
						score={overallComplianceScore}
						size={200}
						showDetails={true}
						loading={loading}
					/>
					<div className="compliance-breakdown">
						<div className="compliance-breakdown__item">
							<span className="compliance-breakdown__label">Documentation</span>
							<span className="compliance-breakdown__value">
								{executiveOverview?.documentation_score ?? 0}%
							</span>
						</div>
						<div className="compliance-breakdown__item">
							<span className="compliance-breakdown__label">Controls</span>
							<span className="compliance-breakdown__value">
								{executiveOverview?.controls_score ?? 0}%
							</span>
						</div>
						<div className="compliance-breakdown__item">
							<span className="compliance-breakdown__label">Evidence</span>
							<span className="compliance-breakdown__value">
								{executiveOverview?.evidence_score ?? 0}%
							</span>
						</div>
					</div>
				</WidgetContainer>

				{/* Pending Decisions */}
				<WidgetContainer
					title="Decisions Required"
					onAction={() => navigate('/decisions')}
					actionLabel="View All"
					loading={loading}
				>
					<AlertCard
						items={decisionAlerts}
						type="info"
						loading={loading}
						emptyMessage="No pending decisions"
					/>
				</WidgetContainer>
			</DashboardGrid>

			{/* Risk Summary and Compliance Trend Row */}
			<DashboardGrid columns={2}>
				{/* Risk Summary */}
				<WidgetContainer
					title="Risk Summary"
					onAction={() => navigate('/risk')}
					actionLabel="View Details"
					loading={loading}
				>
					<div className="risk-summary-container">
						<div className="risk-summary__chart">
							<DonutChart
								data={riskDistribution}
								height={200}
								innerRadius={45}
								outerRadius={75}
								showLegend={false}
								loading={loading}
							/>
						</div>
						<div className="risk-summary__stats">
							<div className="risk-stat">
								<span className="risk-stat__value risk-stat__value--high">
									{riskSummary?.high_risk_items || 0}
								</span>
								<span className="risk-stat__label">High Risk</span>
							</div>
							<div className="risk-stat">
								<span className="risk-stat__value risk-stat__value--medium">
									{riskSummary?.medium_risk_items || 0}
								</span>
								<span className="risk-stat__label">Medium Risk</span>
							</div>
							<div className="risk-stat">
								<span className="risk-stat__value risk-stat__value--low">
									{riskSummary?.low_risk_items || 0}
								</span>
								<span className="risk-stat__label">Low Risk</span>
							</div>
						</div>
					</div>
				</WidgetContainer>

				{/* Compliance Trend */}
				<WidgetContainer
					title="Compliance Trend (30 Days)"
					loading={loading}
				>
					<LineChart
						data={complianceTrendData}
						lines={trendLines}
						xAxisKey="date"
						height={280}
						showGrid={true}
						showLegend={false}
						loading={loading}
					/>
				</WidgetContainer>
			</DashboardGrid>

			{/* Quick Actions */}
			<QuickActions actions={quickActions} />
		</div>
	)
}

export default ExecutiveDashboard
